// @ts-strict-ignore
import type { Option } from '@usealloy/component-library';
import type { FormState, OutcomeOption } from './types';
import type {
  Category, Outcome, OutcomeObjectInTag, TagType,
} from '~/dashboard/views/workflows/store/engine/types';
import type { NodeEditorFormAdapter } from '../useNodeEditorForm';
import {
  SUPPRESSION_UNIT_OPTIONS,
} from './constants';

// formatters:
const snakeCaseToSpaceSeparated = (variable: string) => variable && variable.split('_').join(' ');

// transform:
export const convertOutcomeToTagOutcome = (outcome: Outcome): OutcomeObjectInTag => ({
  outcome: outcome.outcome,
  color: outcome.outcome_color,
  archived: outcome.archived,
  isCaseStatus: outcome.is_case_status,
  isEphemeral: outcome.is_ephemeral ?? false,
});

// build option:
export const buildOption = (value: string): Option => ({
  label: value,
  value,
});

export const buildCategoryOption = (category: Category): Option => ({
  label: snakeCaseToSpaceSeparated(category.name),
  value: category.category_id.toString(),
});

export const buildOutcomesOptions = (
  outcomes: Outcome[] | OutcomeObjectInTag[],
  isOutcomesFromTag: boolean = false,
): OutcomeOption[] => outcomes.map(outcome => ({
  label: outcome.outcome,
  meta: isOutcomesFromTag ? outcome : convertOutcomeToTagOutcome(outcome),
  value: outcome.outcome,
}));

// get options:
export const getOutcomeOptions = (outcomes: Outcome[]): OutcomeOption[] => {
  const unarchivedOutcomes = outcomes.filter(outcome => !outcome.archived);
  return buildOutcomesOptions(unarchivedOutcomes);
};

export const getCategoryOptions = (
  categories: Category[],
): Option[] => [...categories.map(buildCategoryOption)];

export const getCaseTypeOptions = (
  tagTypes: TagType[],
): Option[] => [...tagTypes.map(tagType => buildOption(tagType.name))];

export const getSuppressedUnits = (): Option[] => SUPPRESSION_UNIT_OPTIONS;

// Helper to check if suppressionInterval has valid values
const isValidSuppressionInterval = (
  suppressionInterval: { unit: string; value: number } | null | undefined,
): boolean => {
  if (!suppressionInterval) return false;
  return suppressionInterval.unit != null && suppressionInterval.value != null;
};

export const tagGlobalSettingsFormAdapter: NodeEditorFormAdapter<FormState> = {
  getDefaultFormValues(action) {
    const { tag } = action;

    const caseTypeOption = tag.associatedTypes?.length
      ? buildOption(tag.associatedTypes[0])
      : null;

    const categoryOption = tag.categoryName
      ? buildCategoryOption({
        name: tag.categoryName, category_id: tag.categoryId,
      })
      : null;

    const outcomesOptions = buildOutcomesOptions(tag.outcomes, true);

    // Normalize suppressionInterval: if it has null values, treat it as null
    const validSuppressionInterval = isValidSuppressionInterval(tag.suppressionInterval)
      ? tag.suppressionInterval
      : null;

    return {
      name: tag.name,
      color: tag.color,
      description: tag.description,
      category: categoryOption,
      outcomes: outcomesOptions,
      isAdverseActionReason: tag.isAdverseActionReason,
      isCaseCreating: tag.isCaseCreating,
      caseType: caseTypeOption,
      isSuppressed: !!validSuppressionInterval,
      suppressedUnit: validSuppressionInterval
        ? SUPPRESSION_UNIT_OPTIONS.find(option => option.value === validSuppressionInterval.unit)
        : null,
      suppressedValue: validSuppressionInterval
        ? validSuppressionInterval.value
        : null,
    };
  },
  mapFormDataToAction(data, sourceAction) {
    const caseType = data.caseType?.value ? [data.caseType.value] : [];
    const categoryId = data.category?.value ? Number(data.category.value) : null;
    const categoryName = data.category?.value ? data.category.label : null;

    return {
      ...sourceAction,
      tag: {
        ...sourceAction.tag,
        name: data.name,
        color: data.color,
        description: data.description,
        categoryId,
        categoryName,
        outcomes: data.outcomes.map(o => o.meta),
        isAdverseActionReason: data.isAdverseActionReason,
        isCaseCreating: data.isCaseCreating,
        associatedTypes: data.isCaseCreating ? caseType : [],
        suppressionInterval: data.isSuppressed
          ? {
            unit: data.suppressedUnit.value,
            value: data.suppressedValue ? Number(data.suppressedValue) : null,
          }
          : null,
      },
    };
  },
};
